import { useQuery } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistanceToNow } from "date-fns";
import { CreditCard, IndianRupee, CheckCircle, XCircle, Clock } from "lucide-react";

interface Payment {
  id: number;
  amount: number;
  currency: string;
  status: string;
  paymentType: string;
  planCode?: string;
  razorpayPaymentId?: string;
  createdAt: string;
  updatedAt: string;
}

export function PaymentsTable() {
  const { data: payments, isLoading } = useQuery<Payment[]>({
    queryKey: ["/api/billing/history"],
  });

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!payments || payments.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No Payment History</h3>
          <p className="text-muted-foreground text-center">
            Your subscription and payment history will appear here once you make your first payment.
          </p>
        </CardContent>
      </Card>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "captured": 
      case "paid": 
      case "completed": 
        return "bg-green-100 text-green-800";
      case "failed": 
      case "error": 
        return "bg-red-100 text-red-800";
      case "pending": 
      case "created": 
        return "bg-yellow-100 text-yellow-800";
      default: 
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case "captured": 
      case "paid": 
      case "completed": 
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "failed": 
      case "error": 
        return <XCircle className="h-4 w-4 text-red-500" />;
      case "pending": 
      case "created": 
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default: 
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const formatPaymentType = (type: string) => {
    switch (type) {
      case "subscription": return "Subscription";
      case "one_time": return "Add-on Credits";
      case "addon": return "Add-on Credits";
      default: return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
    }).format(amount / 100); // Razorpay amounts are in paise
  };

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Payment</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Plan</TableHead>
            <TableHead>Amount</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {payments.map((payment) => (
            <TableRow key={payment.id}>
              <TableCell>
                <div className="space-y-1">
                  <div className="font-medium">
                    #{payment.id}
                  </div>
                  {payment.razorpayPaymentId && (
                    <div className="text-xs text-muted-foreground font-mono">
                      {payment.razorpayPaymentId}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Badge variant="outline">
                  {formatPaymentType(payment.paymentType)}
                </Badge>
              </TableCell>
              <TableCell>
                {payment.planCode ? (
                  <Badge variant="secondary">
                    {payment.planCode}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <IndianRupee className="h-4 w-4" />
                  <span className="font-medium">
                    {formatAmount(payment.amount, payment.currency)}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getStatusIcon(payment.status)}
                  <Badge 
                    variant="secondary" 
                    className={`text-xs ${getStatusColor(payment.status)}`}
                  >
                    {payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                  </Badge>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm text-muted-foreground">
                  {formatDistanceToNow(new Date(payment.createdAt), { addSuffix: true })}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {payments.length >= 50 && (
        <div className="text-center text-sm text-muted-foreground">
          Showing last 50 payments
        </div>
      )}
    </div>
  );
}
